import axios from "axios";
import store from "../../store";

// The deployment target comes from VUE_APP_ENV ("development" | "hml" | "production"),
// never from NODE_ENV. NODE_ENV is webpack's build-optimisation flag — keying the API host
// off it forces every deployed image to be built in development mode just to resolve the
// right host, which ships an unminified, eval-wrapped bundle several times its real size.
// The `|| VUE_APP_HML_API_URL` fallback covers env files that still carry the API host
// under the HML key; drop it once every APP_ENV_FILE defines VUE_APP_PRODUCTION_API_URL.
const URL =
  process.env.VUE_APP_ENV === "production"
    ? process.env.VUE_APP_PRODUCTION_API_URL || process.env.VUE_APP_HML_API_URL
    : process.env.VUE_APP_HML_API_URL || null

export const apiClient = axios.create({
  baseURL: URL
});

// vuex-persistedstate writes its whole slice under this single key, so a `storage` event
// naming it is how one tab learns another rotated the token.
const PERSISTED_STATE_KEY = "vuex";

// The API returns expires_in with every token, and nothing used it: the session was only
// ever renewed *after* a 401. That makes every expiry arrive as a simultaneous burst of
// failures across every polling view. Renewing slightly early turns the burst into one
// quiet request, and the margin also absorbs clock drift against the identity provider.
const REFRESH_SKEW_MS = 60 * 1000;

let expiresAt = 0;
let proactiveTimer = null;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const currentAccessToken = () => store.state.users.user?.access_token || null;

// The refresh call travels through this same client, so it reaches the response handler
// like any other request. Without this it could be queued behind its own refresh and wait
// forever.
const isRefreshCall = config =>
  typeof config?.url === "string" && config.url.includes("/refresh-token");

function cancelProactiveRefresh() {
  if (proactiveTimer) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

function scheduleProactiveRefresh() {
  cancelProactiveRefresh();
  if (!expiresAt) return;

  const delay = expiresAt - Date.now() - REFRESH_SKEW_MS;
  // Already inside the margin — a reload of a nearly-expired session, say. Leave it to the
  // next 401 rather than firing a refresh during application start-up.
  if (delay <= 0) return;

  proactiveTimer = setTimeout(() => {
    refreshAccessToken().catch(() => {
      // Not actionable here: the next request's 401 runs the same path and surfaces the
      // failure where a caller can react to it.
    });
  }, delay);
}

// Globo ID rotates the refresh token, invalidating the previous one. Two tabs refreshing
// at once therefore means whichever finishes second presents a token already spent, gets
// a 403, and logs the user out of both. Web Locks provides a real cross-tab mutex; the
// token re-check inside it is what makes waiting worthwhile, because by the time the loser
// acquires the lock the winner has already stored a usable token.
async function refreshAccessToken() {
  const tokenBefore = currentAccessToken();

  const run = async () => {
    const now = currentAccessToken();
    if (now && now !== tokenBefore) {
      return now;
    }

    const refresh_token = store.state.users.user?.refresh_token || null;
    const { data } = await apiClient.post(`/refresh-token`, { refresh_token });

    store.dispatch("users/setUserData", data);
    apiClient.defaults.headers.common["Authorization"] = "Bearer " + data.access_token;

    return data.access_token;
  };

  if (navigator.locks?.request) {
    return navigator.locks.request("socialcam-token-refresh", run);
  }
  return run();
}

// `store` imports this module back (store/modules/users.js needs apiClient), so `store` is
// still undefined while this file is being evaluated. Wiring the listeners on first use
// instead of at import time sidesteps the cycle.
let lifecycleWired = false;

function wireTokenLifecycle() {
  if (lifecycleWired) return;
  lifecycleWired = true;

  // Login and refresh both land on this mutation and both carry expires_in. getUserData
  // commits a user record without it, which is why the field itself is the gate.
  store.subscribe(mutation => {
    if (mutation.type !== "users/setUserData") return;

    if (!mutation.payload) {
      expiresAt = 0;
      cancelProactiveRefresh();
      return;
    }

    if (typeof mutation.payload.expires_in === "number") {
      expiresAt = Date.now() + mutation.payload.expires_in * 1000;
      scheduleProactiveRefresh();
    }
  });

  window.addEventListener("storage", event => {
    if (event.key !== PERSISTED_STATE_KEY) return;

    let next = null;
    try {
      next = JSON.parse(event.newValue || "null")?.users?.user || null;
    } catch (parseError) {
      return;
    }

    const current = store.state.users.user;

    // Another tab logged out. Following it stops this one polling with a dead token. The
    // `current` check also terminates the cascade: once logged out there is no user left,
    // so the storage events that logout itself emits are ignored.
    if (!next && current) {
      store.dispatch("users/logout");
      return;
    }

    // Another tab refreshed. Adopt its token rather than spending our own refresh token on
    // a rotation that has already happened.
    if (next && next.access_token !== current?.access_token) {
      store.commit("users/setUserData", next);
      apiClient.defaults.headers.common["Authorization"] = "Bearer " + next.access_token;
      if (isRefreshing) {
        processQueue(null, next.access_token);
      }
    }
  });
}

apiClient.interceptors.request.use(
  config => {
    wireTokenLifecycle();

    const access_token = store.state.users.user?.access_token || null

    if (access_token) {
      config.headers['Authorization'] = 'Bearer ' + access_token;
    }

    // Multi-site scope: tag every request with the selected GloboGraphics site
    // (sigla) when one is chosen, so Vizrt/playout requests route to that site's
    // MSE/engine pool. Non-Vizrt endpoints simply ignore the header.
    const siteSigla = store.state.sites?.selectedSiteSigla || null;
    if (siteSigla) {
      config.headers['x-globographics-site'] = siteSigla;
    }
    return config;
  },
);

apiClient.interceptors.response.use(
  response => responseSuccessHandler(response),
  error => responseErrorHandler(error)
);

const responseSuccessHandler = response => {
  return response;
};

const responseErrorHandler = error => {
  const originalRequest = error.config;
  const status = error.response?.status;

  // A request that never received a response — network drop, timeout, CORS rejection, a pod
  // restarting mid-flight — carries no error.response at all. Reading .status off it threw
  // a TypeError inside this handler, which replaced the real error with that TypeError and
  // left the request neither retried nor reported.
  if (!status || !originalRequest) {
    return Promise.reject(error);
  }

  if (status === 401 && !originalRequest._retry && !isRefreshCall(originalRequest)) {
    // Marked before queueing as well as before refreshing. Without it a replayed request
    // that 401s again re-enters this branch and starts a second refresh.
    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise(function (resolve, reject) {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return apiClient(originalRequest);
        })
        .catch(error => {
          return Promise.reject(error);
        });
    }

    isRefreshing = true;

    return refreshAccessToken()
      .then(token => {
        processQueue(null, token);
        originalRequest.headers['Authorization'] = 'Bearer ' + token;
        return apiClient(originalRequest);
      })
      .catch(refreshError => {
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      })
      .finally(() => {
        isRefreshing = false;
      });
  }

  if (status === 403) {
    store.dispatch("users/logout");
  }

  return Promise.reject(error);
};
